import { useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { getPortalData } from '@bynder/portal-and-user-data';
import { getAppCodes, getModulePermissions } from '../../helpers/decodeJWT';
import { API } from '../../helpers/api';
import { isAccessTokenRetrievalError } from '../../helpers/isAccessTokenRetrievalError';

export const APP_CODES = {
	bynderAI: 'BAI',
};

// todo: move to a shared constants file if used in multiple places
export const PORTAL_FEATURES = {
	genAI: '45eed470-746c-4c53-a80a-1742507a325c',
};

export const PERMISSIONS = {
	super: ['superadmin', 'superlight', 'reseller'] as const,
	generateWithPrompt: 'bynder_ai.genai.generate_with_prompt',
	downloadOriginal: 'asset_bank.asset.original.download',
	tools: {
		REMOVE_BACKGROUND: 'bynder_ai.genai.remove_background',
		REPLACE_BACKGROUND: 'bynder_ai.genai.generate_background',
		RESIZE: 'bynder_ai.genai.resize_image',
		SMART_EDIT: 'bynder_ai.genai.edit_with_prompt',
	},
	create: {
		dam: 'asset_bank.asset.create',
		waitingroom: 'asset_bank.asset.waitingroom.create',
	},
} as const;

export function useImageGenUserPermissions() {
	const [permissions, setPermissions] = useState<Permissions>(new Set());

	useEffect(() => {
		fetchPermissions().then(setPermissions);
	}, []);

	const isSuperUser = PERMISSIONS.super.some((permission) => permissions.has(permission));

	const allowedTools = useMemo(
		() =>
			Object.entries(PERMISSIONS.tools)
				.filter(([, permission]) => isSuperUser || permissions.has(permission))
				.map(([toolName]) => toolName as keyof typeof PERMISSIONS.tools),
		[permissions, isSuperUser],
	);

	const isApprovalRequired = !isSuperUser && permissions.has(PERMISSIONS.create.waitingroom);
	const canCreateAssets = isSuperUser || permissions.has(PERMISSIONS.create.dam);
	const canGenerateWithPrompt = isSuperUser || permissions.has(PERMISSIONS.generateWithPrompt);
	const canDownloadOriginalAssets = isSuperUser || permissions.has(PERMISSIONS.downloadOriginal);

	return {
		allowedTools,
		isApprovalRequired,
		canCreateAssets,
		canGenerateWithPrompt,
		canDownloadOriginalAssets,
	};
}

export type ImageGenUserPermissions = ReturnType<typeof useImageGenUserPermissions>;
type Permissions = Set<string>;

const EMPTY_PERMISSIONS: Permissions = new Set();

async function fetchPermissions(): Promise<Permissions> {
	try {
		const [appCodes, modulePermissions] = await Promise.all([
			getAppCodes(),
			getModulePermissions(),
		]);

		if (!appCodes.includes(APP_CODES.bynderAI)) {
			return EMPTY_PERMISSIONS;
		}

		// Check features only if the portal has Bynder AI app code, to avoid unnecessary API calls
		const features = await getFeatures();

		if (!features.includes(PORTAL_FEATURES.genAI)) {
			return EMPTY_PERMISSIONS;
		}

		return new Set(modulePermissions);
	} catch (error) {
		if (!isAccessTokenRetrievalError(error)) {
			logError(error);
		}

		return EMPTY_PERMISSIONS;
	}
}

async function getFeatures(): Promise<string[]> {
	const portalData = await getPortalData();

	if (!portalData) {
		logError(new Error('Failed to get portal data'));

		return [];
	}

	return API.getPortalFeatures(portalData.id);
}

function logError(error: unknown) {
	if (process.env.NODE_ENV === 'production') {
		captureException(error);
	} else {
		console.error(error);
	}
}
